<!-- PLAY — the mode picker, and where the app opens.

     Seven different STYLES of play, not seven topics. Endurance gets a wide
     tile at the top because it is the headline feature and burying it behind a
     tab bar icon would be hiding the best thing in the app. -->
<template>
  <IonPage>
    <AppHeader title="Play">
      <IonButtons slot="end">
        <!-- The `sr-only` span as well as the aria-label, per the note in
             ionic.css: Ionic moves an aria-label off the host and onto the
             button inside its shadow root, so every tool that reads the light
             DOM — the factory's own UX audit included — sees an icon-only
             control with no name at all. -->
        <IonButton @click="go('/tabs/play/topics')" aria-label="Topics and settings">
          <IonIcon slot="icon-only" :icon="optionsOutline" />
          <span class="sr-only">Topics and settings</span>
        </IonButton>
      </IonButtons>
    </AppHeader>

    <IonContent :fullscreen="true">
      <AppLargeTitle title="Play" />

      <div class="ex-total">
        <div>
          <div class="k">Austin's total</div>
          <div class="n tabular">{{ (profile.totalScore ?? 0).toLocaleString() }}</div>
        </div>
        <div class="center">
          <div class="k">Best run</div>
          <div class="n" :class="{ tabular: hasBest }" :style="hasBest ? null : 'font-size: var(--text-lg)'">
            {{ bestLabel }}
          </div>
        </div>
      </div>

      <p class="ex-eyebrow pad">The big one</p>
      <div class="mode-grid">
        <button
          type="button"
          class="mode-tile wide"
          :style="tileStyle(ENDURANCE_MODE)"
          @click="go(ENDURANCE_MODE.route)"
        >
          <IonIcon class="tile-icon" :icon="ENDURANCE_MODE.icon" aria-hidden="true" />
          <span class="name">{{ ENDURANCE_MODE.name }}</span>
          <span class="blurb">{{ ENDURANCE_MODE.blurb }}</span>
        </button>
      </div>

      <p class="ex-eyebrow pad" style="margin-top: var(--sp-5)">Pick your poison</p>
      <div class="mode-grid">
        <button
          v-for="m in MODES"
          :key="m.id"
          type="button"
          class="mode-tile"
          :style="tileStyle(m)"
          @click="go(m.route)"
        >
          <IonIcon class="tile-icon" :icon="m.icon" aria-hidden="true" />
          <span class="name">{{ m.name }}</span>
          <span class="blurb">{{ m.blurb }}</span>
        </button>
      </div>

      <!-- HOW TO PLAY, ON THE SCREEN HE OPENS INTO.
           The help sheet shipped with the chassis and nothing in the app ever
           opened it, so eight modes with eight different penalties had no
           single place that explained them. Quiet, under the tiles, where it
           does not compete with the thing he actually came here to tap. -->
      <div class="pad" style="margin-top: var(--sp-4)">
        <IonButton fill="clear" size="small" @click="openHelp">
          <IonIcon slot="start" :icon="helpCircleOutline" aria-hidden="true" />
          How to play
        </IonButton>
      </div>

      <IonList v-if="recent.length" inset class="list-inset" style="margin-top: var(--sp-5)">
        <IonListHeader>
          <IonLabel>
            <h3>Last few goes</h3>
            <p>Every mode feeds the same total.</p>
          </IonLabel>
        </IonListHeader>

        <IonItem v-for="r in recent" :key="r.id" lines="full">
          <IonLabel>
            <h3>{{ modeName(r.mode) }}</h3>
            <p>{{ r.correct ?? 0 }} right &middot; best streak {{ r.bestChain ?? 0 }}</p>
          </IonLabel>
          <IonNote slot="end" class="tabular">{{ (r.score ?? 0).toLocaleString() }}</IonNote>
        </IonItem>
      </IonList>

      <!-- THE FIRST SCREEN OF THE FIRST RUN, WHICH IS THE ONE NOBODY LOOKS AT
           TWICE. This was a list row with a pun in it — a header promising
           "Last few goes", then a single line that looked like a run he could
           not remember playing. An empty state says why it is empty and points
           at the one thing that fixes it. No button on it: the fix is the
           seven tiles directly above, and a third route to the same tap is
           noise. -->
      <EmptyState
        v-else
        glyph="💨"
        :title="nudge"
        body="Finish a go in any mode and it lands here. They all feed the one total."
      />

      <div class="tail" />
    </IonContent>

    <HelpModal v-model="helpOpen" />
  </IonPage>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import {
  IonPage, IonContent, IonButtons, IonButton, IonIcon,
  IonList, IonListHeader, IonItem, IonLabel, IonNote,
  useIonRouter,
} from '@ionic/vue';
import { optionsOutline, helpCircleOutline } from 'ionicons/icons';
import { AppHeader, AppLargeTitle } from '../core/ui';
import HelpModal from '../components/HelpModal.vue';
import EmptyState from '../components/EmptyState.vue';
import { MODES, ENDURANCE_MODE, modeName } from '../game/modes.js';
import { TABS } from '../router.js';
import { profile, ensureProfile } from '../game/profile.js';
import { runs } from '../store.js';
import { spellDuration } from '../game/engine.js';
import { pun } from '../game/puns.js';
import { haptic } from '../core/util.js';

const router = useRouter();
const ionRouter = useIonRouter();

// The Endurance tile goes to a TAB, and every other tile pushes a screen
// inside this one. See `go()`.
const TAB_PATHS = new Set(TABS.map((t) => t.path));

const helpOpen = ref(false);

function openHelp() {
  haptic('pop');
  helpOpen.value = true;
}

function go(path) {
  // A tile is the loudest control in the app and it was the only one that
  // gave nothing back on touch besides the CSS press.
  haptic('pop');

  // A TAB IS NOT A PUSH, AND `router.push` COULD NOT TELL THE DIFFERENCE.
  //
  // The big tile goes to /tabs/endurance, which is a tab root. Pushing it
  // stacked the Endurance screen INSIDE the Play tab: the tab bar went on
  // highlighting Play while Endurance filled the screen, the page came up
  // `can-go-back` with no back button on it (a tab root has no `back` prop,
  // correctly), and the only way out was a tab bar that was pointing at the
  // wrong tab. Ionic only reassigns the tab when the navigation is a root one,
  // which is exactly what tapping the tab button does — so do that.
  if (TAB_PATHS.has(path)) ionRouter.navigate(path, 'root', 'replace');
  else router.push(path);
}

/**
 * Both halves of the tile's colour pair.
 *
 * The foreground is NOT always white. Measured against WCAG AA, six of the
 * eight tints failed with white for the blurb line - white on --tint-sort is
 * 2.57:1 against the 4.5:1 body text needs. The correct partner per tint is
 * recorded in app.css as --tint-fg-*; this just picks it up alongside the
 * background so the two can never be set independently and drift apart.
 */
const tileStyle = (m) => ({
  '--tile': `var(--tint-${m.tint})`,
  '--tile-fg': `var(--tint-fg-${m.tint})`,
});

// Rolled once on mount rather than in the template: a computed pun would
// re-roll on every unrelated re-render, so the empty state would change its
// mind while he was reading it.
const nudge = ref(pun('nudge'));

const recent = computed(() => runs.value.slice(0, 4));

const hasBest = computed(() => (profile.value.bestEnduranceMs ?? 0) > 0);

// "Not yet" rather than an em-dash. A dash is a thing that broke; "Not yet" is
// an invitation, and this is the number the whole app wants him chasing.
const bestLabel = computed(() => {
  const ms = profile.value.bestEnduranceMs ?? 0;
  if (!ms) return 'Not yet';
  return spellDuration(ms)
    .replace(' minutes', 'm').replace(' minute', 'm')
    .replace(' seconds', 's').replace(' second', 's');
});

onMounted(ensureProfile);
</script>
